import * as React from 'react';
import { ApiVersionInfo, getApiVersion } from '@app/api';
import { useApiHealthStore } from '@app/store/apiHealthStore';

/**
 * The version of the backend, read once per session.
 *
 * Two things display it — the About dialog and the version stamp at the foot of the sidebar
 * — and both are mounted at the same time, so the in-flight request is shared rather than
 * made twice. The answer only changes when the service is redeployed, which this tab finds
 * out about by reconnecting, so there is nothing to poll for.
 */

/** Module scope on purpose: the version belongs to the session, not to any one component. */
let cached: ApiVersionInfo | null = null;
let inFlight: Promise<ApiVersionInfo | null> | null = null;

/**
 * Drops the session's answer so the next caller asks the service again. Used when a reconnect
 * makes the held version suspect, and by tests, which would otherwise each inherit whatever the
 * one before them read.
 */
export const forgetApiVersion = () => {
  cached = null;
};

const load = (): Promise<ApiVersionInfo | null> => {
  if (cached) return Promise.resolve(cached);

  // A second caller during the first call's flight waits on it instead of starting its own.
  inFlight ??= getApiVersion()
    .then((info) => {
      cached = info;
      return info;
    })
    // Not knowing the API version is not worth an error anywhere: the callers render
    // nothing in its place. A service too old for the endpoint lands here too.
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** The API version string, or null while it is loading or if it could not be read. */
export const useApiVersion = (): string | null => {
  const [version, setVersion] = React.useState<string | null>(cached?.version ?? null);

  // Bumped when the service comes back after an outage — which is exactly when it is
  // likely to be running a different version from the one we read before.
  const retryToken = useApiHealthStore((state) => state.retryToken);

  React.useEffect(() => {
    let active = true;

    // A reconnect means the cached answer may now be stale; drop it and ask again.
    if (retryToken > 0) forgetApiVersion();

    void load().then((info) => {
      if (active && info) setVersion(info.version);
    });

    return () => {
      active = false;
    };
  }, [retryToken]);

  return version;
};
